// @ts-nocheck

import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

interface CommLog {
  id: string;
  type: "email" | "sms";
  status: string;
  recipient_email?: string | null;
  recipient_phone?: string | null;
  subject?: string | null;
  template_name?: string | null;
  created_at: string;
  metadata?: Record<string, any> | null;
}

const statusVariant = (status: string) => {
  switch (status) {
    case "pending":
      return "secondary" as const;
    case "sent":
      return "default" as const;
    case "failed":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
};

const CommunicationLogsTab: React.FC = () => {
  const [logs, setLogs] = useState<CommLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("communication_logs")
        .select("id,type,status,recipient_email,recipient_phone,subject,template_name,created_at,metadata")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      setLogs(data || []);
    } catch (e) {
      console.error("Error loading communication logs", e);
      toast.error("Failed to load communication logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filtered = logs.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      l.recipient_email?.toLowerCase().includes(q) ||
      l.recipient_phone?.toLowerCase().includes(q) ||
      l.subject?.toLowerCase().includes(q) ||
      l.template_name?.toLowerCase().includes(q) ||
      JSON.stringify(l.metadata || {}).toLowerCase().includes(q)
    );
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Communication Logs</CardTitle>
        <div className="flex gap-2 w-full md:w-auto md:items-center md:justify-end">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search recipient, subject, template..."
              className="pl-8"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject / Template</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No communications found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="capitalize">{log.type}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(log.status)}>{log.status}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate">
                      {log.recipient_email || log.recipient_phone || "-"}
                    </TableCell>
                    <TableCell className="max-w-[360px] truncate">
                      {log.subject || log.template_name || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunicationLogsTab;

